import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ListParams } from "../../../admin-api/games";
import type {
	AdminGenderRequest,
	AdminProfileRequest,
	AdminSkillRequest,
} from "../../../admin-api/profiles";
import { adminApi } from "../../../api-client";
import { queryKeys } from "../../query-keys";

export function useProfiles(params: ListParams = {}, enabled = true) {
	return useQuery({
		enabled,
		placeholderData: keepPreviousData,
		queryFn: () => adminApi.profiles.list(params),
		queryKey: queryKeys.profiles.list(params),
	});
}

export function useProfile(id: string | null) {
	return useQuery({
		enabled: !!id,
		queryFn: () => adminApi.profiles.get(id ?? ""),
		queryKey: queryKeys.profiles.detail(id ?? ""),
	});
}

// Every admin edit invalidates the list as well as the detail: the list row
// reads its name, avatar and ban badge from the same payload, so an admin who
// edits from the detail page would otherwise land back on a stale list.
function useProfileMutation<TArgs, TResult>(id: string, run: (args: TArgs) => Promise<TResult>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: run,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.profiles.detail(id) });
			void queryClient.invalidateQueries({ queryKey: queryKeys.profiles.all });
		},
	});
}

export function useBanProfile(id: string) {
	return useProfileMutation(id, (reason: string) => adminApi.profiles.ban(id, reason));
}

export function useUnbanProfile(id: string) {
	return useProfileMutation<void, unknown>(id, () => adminApi.profiles.unban(id));
}

export function useUpdateProfile(id: string) {
	return useProfileMutation(id, (body: AdminProfileRequest) => adminApi.profiles.update(id, body));
}

export function useUpdateProfileGender(id: string) {
	return useProfileMutation(id, (body: AdminGenderRequest) =>
		adminApi.profiles.updateGender(id, body),
	);
}

export function useUpdateProfileSkill(id: string) {
	return useProfileMutation(id, (body: AdminSkillRequest) =>
		adminApi.profiles.updateSkill(id, body),
	);
}

export function useDeleteProfileAvatar(id: string) {
	return useProfileMutation<void, unknown>(id, () => adminApi.profiles.deleteAvatar(id));
}

export function useDeleteProfileBanner(id: string) {
	return useProfileMutation<void, unknown>(id, () => adminApi.profiles.deleteBanner(id));
}

// The reset answers with the updated profile, and the shared invalidation
// refetches detail and list, so the "Onboarding" badge on both flips together.
export function useResetProfileOnboarding(id: string) {
	return useProfileMutation<void, unknown>(id, () => adminApi.profiles.resetOnboarding(id));
}

// Deleting anonymises the account into a tombstone, so the detail query it
// invalidates will refetch a 404 - callers route away on success.
export function useDeleteProfile(id: string) {
	return useProfileMutation<void, void>(id, () => adminApi.profiles.delete(id));
}
